import { useEffect, useRef, useState } from "react";

type Props = {
  url: string;
  /** Duración conocida (segundos); si no, se lee del video. */
  duration?: number | null;
  /** Porcentaje ya visto en visitas anteriores. */
  initialPercent?: number;
  /** Segundo donde dejó el video la última vez. */
  startAt?: number;
  onStart: () => void;
  /** percent = % realmente visto (los saltos adelante no cuentan). */
  onProgress: (percent: number, position: number, milestone: boolean) => void;
  onEnded: () => void;
  /** Si se indica, el video se pausa al llegar a este % y se avisa con onGate (captura del WhatsApp a mitad del video). */
  gateAtPercent?: number | null;
  onGate?: () => void;
  /** Cada vez que cambia (y es > 0) el video continúa reproduciéndose. */
  resumeKey?: number;
};

const MILESTONES = [25, 50, 75, 100];

function youTubeId(url: string): string | null {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/))([\w-]{11})/);
  return m ? m[1] : null;
}

type YTPlayer = {
  getCurrentTime: () => number;
  getDuration: () => number;
  seekTo: (s: number, allow: boolean) => void;
  pauseVideo: () => void;
  playVideo: () => void;
  destroy: () => void;
};
type YTWindow = Window & { YT?: { Player: new (el: HTMLElement, cfg: unknown) => YTPlayer; PlayerState: Record<string, number> }; onYouTubeIframeAPIReady?: () => void };

let ytApi: Promise<void> | null = null;
function loadYouTubeApi(): Promise<void> {
  const w = window as YTWindow;
  if (w.YT?.Player) return Promise.resolve();
  if (!ytApi) {
    ytApi = new Promise((resolve) => {
      const prev = w.onYouTubeIframeAPIReady;
      w.onYouTubeIframeAPIReady = () => {
        prev?.();
        resolve();
      };
      const s = document.createElement("script");
      s.src = "https://www.youtube.com/iframe_api";
      s.async = true;
      document.head.appendChild(s);
    });
  }
  return ytApi;
}

/**
 * Reproductor con seguimiento: mide el tiempo realmente visto (no el punto del video),
 * avisa al cruzar 25/50/75/100 % y manda un latido cada 10 s mientras se reproduce.
 */
export default function WebinarPlayer({ url, duration, initialPercent = 0, startAt = 0, onStart, onProgress, onEnded, gateAtPercent = null, onGate, resumeKey = 0 }: Props) {
  const ytId = youTubeId(url);
  const videoRef = useRef<HTMLVideoElement>(null);
  const ytHost = useRef<HTMLDivElement>(null);
  const [percent, setPercent] = useState(initialPercent);
  const ytPlayerRef = useRef<YTPlayer | null>(null);
  const gate = useRef({ at: gateAtPercent, fired: false });
  gate.current.at = gateAtPercent;

  // Estado de medición (no provoca renders)
  const st = useRef({ watched: 0, last: -1, total: duration ?? 0, started: false, lastSent: 0, maxSent: initialPercent, ended: false });
  const cb = useRef({ onStart, onProgress, onEnded, onGate });
  cb.current = { onStart, onProgress, onEnded, onGate };

  const pausePlayer = () => {
    try {
      videoRef.current?.pause();
      ytPlayerRef.current?.pauseVideo();
    } catch {
      // nada
    }
  };

  // Reanuda la reproducción cuando la página lo pide (después de dejar el WhatsApp).
  useEffect(() => {
    if (!resumeKey) return;
    try {
      void videoRef.current?.play();
      ytPlayerRef.current?.playVideo();
    } catch {
      // el navegador puede exigir un toque: el usuario usa el botón de play
    }
  }, [resumeKey]);

  useEffect(() => {
    st.current = { watched: ((duration ?? 0) * initialPercent) / 100, last: -1, total: duration ?? 0, started: false, lastSent: 0, maxSent: initialPercent, ended: false };
    setPercent(initialPercent);
  }, [url, duration, initialPercent]);

  /** Procesa una lectura del reproductor (tiempo actual, duración). */
  const tick = (current: number, total: number, playing: boolean) => {
    const s = st.current;
    if (total > 0) s.total = total;
    if (!s.total) return;
    if (playing) {
      if (!s.started) {
        s.started = true;
        cb.current.onStart();
      }
      if (s.last >= 0) {
        const d = current - s.last;
        if (d > 0 && d <= 2.5) s.watched += d; // reproducción normal; saltos no cuentan
      }
    }
    s.last = current;
    const pct = Math.min(100, Math.max(s.maxSent, (s.watched / s.total) * 100));
    if (gate.current.at != null && !gate.current.fired) {
      const posPct = s.total > 0 ? (current / s.total) * 100 : 0;
      if (Math.max(pct, posPct) >= gate.current.at) {
        gate.current.fired = true;
        pausePlayer();
        cb.current.onGate?.();
      }
    }
    const shown = Math.floor(pct);
    setPercent((p) => (shown > p ? shown : p));
    const crossed = MILESTONES.some((m) => s.maxSent < m && pct >= m);
    const now = Date.now();
    if (playing && (crossed || now - s.lastSent >= 10000)) {
      s.lastSent = now;
      s.maxSent = Math.max(s.maxSent, Math.floor(pct));
      cb.current.onProgress(Math.floor(pct), current, crossed);
    }
  };

  const flush = (current: number) => {
    const s = st.current;
    if (!s.total || !s.started) return;
    const pct = Math.min(100, Math.floor(Math.max(s.maxSent, (s.watched / s.total) * 100)));
    s.maxSent = pct;
    s.lastSent = Date.now();
    cb.current.onProgress(pct, current, false);
  };

  // --- Video directo (mp4 / webm) ---
  useEffect(() => {
    if (ytId) return;
    const v = videoRef.current;
    if (!v) return;
    const onMeta = () => {
      if (startAt > 1 && startAt < (v.duration || Infinity) - 3) v.currentTime = startAt;
      st.current.total = duration && duration > 0 ? duration : v.duration || 0;
    };
    const onTime = () => tick(v.currentTime, duration && duration > 0 ? duration : v.duration || 0, !v.paused && !v.ended);
    const onPause = () => flush(v.currentTime);
    const onEnd = () => {
      st.current.watched = Math.max(st.current.watched, st.current.total * 0.995);
      tick(v.currentTime, st.current.total, true);
      if (!st.current.ended) {
        st.current.ended = true;
        cb.current.onProgress(100, v.currentTime, true);
        cb.current.onEnded();
      }
    };
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("pause", onPause);
    v.addEventListener("ended", onEnd);
    const onHide = () => document.visibilityState === "hidden" && flush(v.currentTime);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("ended", onEnd);
      document.removeEventListener("visibilitychange", onHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, ytId, startAt, duration]);

  // --- YouTube ---
  useEffect(() => {
    if (!ytId || !ytHost.current) return;
    let player: YTPlayer | null = null;
    let timer: number | undefined;
    let cancelled = false;
    let playing = false;
    const mount = document.createElement("div");
    ytHost.current.appendChild(mount);
    loadYouTubeApi().then(() => {
      if (cancelled) return;
      const w = window as YTWindow;
      player = new w.YT!.Player(mount, {
        videoId: ytId,
        width: "100%",
        height: "100%",
        playerVars: { rel: 0, modestbranding: 1, playsinline: 1, start: Math.floor(startAt) || 0 },
        events: {
          onStateChange: (e: { data: number }) => {
            const S = w.YT!.PlayerState;
            playing = e.data === S.PLAYING;
            if (e.data === S.PAUSED && player) flush(player.getCurrentTime());
            if (e.data === S.ENDED && player && !st.current.ended) {
              st.current.ended = true;
              st.current.watched = Math.max(st.current.watched, st.current.total * 0.995);
              tick(player.getCurrentTime(), st.current.total || player.getDuration(), true);
              cb.current.onProgress(100, player.getCurrentTime(), true);
              cb.current.onEnded();
            }
          },
        },
      });
      ytPlayerRef.current = player;
      timer = window.setInterval(() => {
        if (!player) return;
        try {
          tick(player.getCurrentTime(), duration && duration > 0 ? duration : player.getDuration(), playing);
        } catch {
          // el reproductor aún no está listo
        }
      }, 1000);
    });
    return () => {
      cancelled = true;
      ytPlayerRef.current = null;
      if (timer) window.clearInterval(timer);
      try {
        player?.destroy();
      } catch {
        // nada
      }
      mount.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ytId, startAt, duration]);

  return (
    <div>
      <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.45)]">
        {ytId ? (
          <div ref={ytHost} className="absolute inset-0 [&>div]:w-full [&>div]:h-full [&_iframe]:w-full [&_iframe]:h-full"></div>
        ) : (
          <video ref={videoRef} src={url} controls playsInline preload="metadata" className="absolute inset-0 w-full h-full" />
        )}
      </div>
      <div className="mt-3 flex items-center gap-3">
        <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100} aria-label="Avance del webinar">
          <div className="h-full rounded-full bg-[#20C997] transition-all duration-500" style={{ width: `${percent}%` }}></div>
        </div>
        <span className="text-xs font-semibold text-white/70 tabular-nums w-20 text-right">{percent}% visto</span>
      </div>
    </div>
  );
}
